import {useMemo, useEffect, useRef} from 'react';
import {Vector3, Matrix4, Quaternion, BufferGeometry, Float32BufferAttribute, DoubleSide} from 'three';
import {useFrame} from '@react-three/fiber'
import {mergeBufferGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {calculateRotQuat} from './visualmath';
import {useAnimStore} from './index';

export const DeformingElementTraj = (props) => {

	console.log('Rendering element '+props.id)

	var steps = props.rp.length;

	const timeSteps = props.visSettings.timeSteps;
	const lineSteps = props.visSettings.lineSteps;
	const crossSteps = props.visSettings.crossSteps;
	const wSteps = props.visSettings.wSteps;
	const tSteps = props.visSettings.tSteps;
	const rate = props.visSettings.rate;

	let rp = new Vector3();
	let rq = new Vector3();
	let qpNode = new Quaternion();
	let qqNode = new Quaternion();

	let q0 = new Quaternion().fromArray(props.q0);

	let geomArray = [];
	var leafGeom = [];

	for (let i = 0; i < steps; i++){

		rp.fromArray(props.rp[i]);
		rq.fromArray(props.rq[i]);
		qpNode.fromArray(props.qp[i]);
		qqNode.fromArray(props.qq[i]);

		let qp = new Quaternion().multiplyQuaternions(qpNode,q0);
		let qq = new Quaternion().multiplyQuaternions(qqNode,q0);

		leafGeom = leaf(rp,rq,qp,qq,props.style.t,props.style.w,lineSteps,crossSteps,wSteps,tSteps);
		geomArray.push(leafGeom);

	}

	return geomArray;
	
}

const leaf = function (rp,rq,qp,qq,w,t,lineSteps,crossSteps,wSteps,tSteps) {

	var getIndices = function (lineSteps,crossSteps,offset){

		let indices = [];
		const crossStepCount = crossSteps + 1;
	   	
	   	for ( let i = 0; i < lineSteps; i ++ ) {

			for ( let j = 0; j < crossSteps; j ++ ) {

				const a = i * crossStepCount + j + offset;
				const b = i * crossStepCount + j + 1 + offset;
				const c = ( i + 1 ) * crossStepCount + j + 1 + offset;
				const d = ( i + 1 ) * crossStepCount + j + offset;

				// faces one and two
				indices.push( a, b, d );
				indices.push( b, c, d );
			}
		}

		let maxIndex = lineSteps*(crossSteps + 1) + crossSteps + offset;
		let numIndices = lineSteps*crossSteps*2;

		return {indices:indices, maxIndex:maxIndex, numIndices:numIndices};
	}

	var topSurfPos = function (a,b) {
	   
		var x = l.x + nyb.x*t/2 + nzb.x*w*(a-1/2);
	   	var y = l.y + nyb.y*t/2 + nzb.y*w*(a-1/2);		
	   	var z = l.z + nyb.z*t/2 + nzb.z*w*(a-1/2);

	   	return new Vector3(x,y,z);

	}	

	var bottomSurfPos = function (a,b) {
	   
		var x = l.x - nyb.x*t/2 + nzb.x*w*(a-1/2);
		var y = l.y - nyb.y*t/2 + nzb.y*w*(a-1/2);
		var z = l.z - nyb.z*t/2 + nzb.z*w*(a-1/2);

	   	return new Vector3(x,y,z);

	}	

	var leftSurfPos = function (a,b) {
	   
	   var x = l.x - nyb.x*t*(a-1/2) - nzb.x*w/2;
	   var y = l.y - nyb.y*t*(a-1/2) - nzb.y*w/2;
	   var z = l.z - nyb.z*t*(a-1/2) - nzb.z*w/2;

	   return new Vector3(x,y,z);

	}	

	var rightSurfPos = function (a,b) {
	   
	   var x = l.x - nyb.x*t*(a-1/2) + nzb.x*w/2;
	   var y = l.y - nyb.y*t*(a-1/2) + nzb.y*w/2;
	   var z = l.z - nyb.z*t*(a-1/2) + nzb.z*w/2;

	   return new Vector3(x,y,z);

	}

	var frontSurf = function (a, b) {
			   
	   var x = rp.x + (-t/2 + t*b)*nyp.x + (-w/2 + w*a)*nzp.x;
	   var y = rp.y + (-t/2 + t*b)*nyp.y + (-w/2 + w*a)*nzp.y;
	   var z = rp.z + (-t/2 + t*b)*nyp.z + (-w/2 + w*a)*nzp.z;

	   return new Vector3(x,y,z);;

	}


	var backSurf = function (a, b) {
	   
	   var x = rq.x + (-t/2 + t*b)*nyq.x + (-w/2 + w*a)*nzq.x;
	   var y = rq.y + (-t/2 + t*b)*nyq.y + (-w/2 + w*a)*nzq.y;
	   var z = rq.z + (-t/2 + t*b)*nyq.z + (-w/2 + w*a)*nzq.z;

	   return new Vector3(x,y,z);;

	}	

	let vertices = [];
	vertices.top = [];
	vertices.bottom = [];
	vertices.left = [];
	vertices.right = [];
	vertices.front = [];
	vertices.back = [];
	
	let uvs = [];
	uvs.top = [];
	uvs.bottom = [];
	uvs.left = [];
	uvs.right = [];
	uvs.front = [];
	uvs.back = [];
	
	let normals = [];
	normals.top = [];
	normals.bottom = [];
	normals.left = [];
	normals.right = [];
	normals.front = [];
	normals.back = [];

	let el = new Vector3();
	let L = el.subVectors(rq,rp).length();
  
   let rotp = new Matrix4();
   let nxp = new Vector3();
   let nyp = new Vector3();
   let nzp = new Vector3();

   let rotq = new Matrix4();
   let nxq = new Vector3();
   let nyq = new Vector3();
   let nzq = new Vector3();

   // ----------
   // interpolate elastic line
   // ----------
   // get rotation matrices at nodes
   rotp.makeRotationFromQuaternion(qp);
   rotp.extractBasis(nxp,nyp,nzp);
   
   rotq.makeRotationFromQuaternion(qq);
   rotq.extractBasis(nxq,nyq,nzq);

   //interpolate quaternions
   let qb = new Quaternion();
   let rotb = new Matrix4();
   let nxb = new Vector3();
   let nyb = new Vector3();
   let nzb = new Vector3();
   let l = new Vector3();

   for (let i=0; i<=lineSteps; i++) {

   		let b = i/lineSteps;
		let p1 = 2*b**3 - 3*b**2 + 1;
		let p2 = b**3 -2*b**2 + b;
		let p3 = -2*b**3+3*b**2;
		let p4 = b**3 - b**2;

		l.setComponent(0, p1*rp.x + L*p2*nxp.x + p3*rq.x + L*p4*nxq.x); //set x of vector l to ..
   		l.setComponent(1, p1*rp.y + L*p2*nxp.y + p3*rq.y + L*p4*nxq.y); //set y of vector l to ..
   		l.setComponent(2, p1*rp.z + L*p2*nxp.z + p3*rq.z + L*p4*nxq.z); //set z of vector l to ..

		qb.slerpQuaternions(qp,qq,b);
		
		rotb.makeRotationFromQuaternion(qb);
		rotb.extractBasis(nxb,nyb,nzb);
		
		for (let j=0; j<=crossSteps; j++){

			let a = j/crossSteps;

			let p = topSurfPos(a,b);
			let normal = nyb;
			vertices.top.push(p.x,p.y,p.z);
			normals.top.push( normal.x, normal.y, normal.z );
			uvs.top.push( a, b );

			p = bottomSurfPos(a,b);
			normal = nyb;
			vertices.bottom.push(p.x,p.y,p.z);
			normals.bottom.push( normal.x, normal.y, normal.z );
			uvs.bottom.push( a, b );

			p = leftSurfPos(a,b);
			normal = nzb;
			vertices.left.push(p.x,p.y,p.z);
			normals.left.push( normal.x, normal.y, normal.z );
			uvs.left.push( a, b );

			p = rightSurfPos(a,b);
			normal = nzb;
			vertices.right.push(p.x,p.y,p.z);
			normals.right.push( normal.x, normal.y, normal.z );
			uvs.right.push( a, b );

		}

   }

	// front and back faces
	// let tSteps = 2;
	// let wSteps = 2;
	for (let i=0; i<=tSteps; i++){
		let b = i/tSteps;
		for (let j=0; j<=wSteps; j++){
			let a = j/wSteps;
			
			let p = frontSurf(a,b);
			let normal = nxp;
			vertices.front.push(p.x,p.y,p.z);
			normals.front.push(normal.x,normal.y,normal.z);
			uvs.front.push(a,b);

			p = backSurf(a,b);
			normal = nxq;
			vertices.back.push(p.x,p.y,p.z);
			normals.back.push(normal.x,normal.y,normal.z);
			uvs.back.push(a,b);
		}
	}

	// let offset = lineSteps*(crossSteps+1)+crossSteps+1; //counts the highest vertex index

	let indicesCollect = [];
	let indices = [];
	let numIndices = [];
	let maxIndex = [];
	let vertexCount = 0; //total vertex count

	//top
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, 0));
	indicesCollect.push(...indices);
	vertexCount += numIndices;

	//bottom
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;
	
	//left
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;
	
	//right
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;
	
	//front
	( {indices,numIndices,maxIndex} = getIndices(wSteps, tSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;

	//back
	( {indices,numIndices,maxIndex} = getIndices(wSteps, tSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;

	const geometry = new BufferGeometry();
	geometry.setIndex(indicesCollect);
	geometry.setAttribute('position', new Float32BufferAttribute([...vertices.top,...vertices.bottom,...vertices.left,...vertices.right,...vertices.front,...vertices.back],3) );
	geometry.setAttribute('normal', new Float32BufferAttribute([...normals.top,...normals.bottom,...normals.left,...normals.right,...normals.front,...normals.back],3) );
	// geometry.setAttribute('uv', new Float32BufferAttribute([...uvs.top, ...uvs.bottom],2) );

	return geometry;
	
}